'use client';

import { departmentsSection } from '@stereoagency/sanity-schema';
import {
  DepartmentsSection as UIDepartmentsSection,
  DepartmentsSectionDepartmentType
} from '@stereoagency/ui';
import flush from 'just-flush';
import useTranslation from 'next-translate/useTranslation';
import { defaultLanguage } from '@stereoagency/constants';
import { useState } from 'react';
import { profilesQuery } from './profilesQuery';
import { findInKeyArray, formatProfileName, sanityImageAsProps } from '@/utils';
import { ProfileModal } from '@/components/modals';

type SingleProfile = NonNullable<(typeof profilesQuery)[1]>[number];

type Props = {
  title?: string;
  subtitle?: string;
  departments?: (typeof departmentsSection.schema.departmentsSection)['departments'];
  profiles?: (typeof profilesQuery)[1];
};

export default function DepartmentsSection({
  title,
  subtitle,
  departments,
  profiles
}: Props) {
  const { t } = useTranslation();
  const { lang } = useTranslation();
  const [profileOpen, setProfileOpen] = useState<SingleProfile | null>(null);

  const entries = flush(
    departments?.map<DepartmentsSectionDepartmentType | null>((dep) => {
      type PrimaryMemberType = NonNullable<
        DepartmentsSectionDepartmentType['primaryMembers']
      >[number];
      type OtherMemberType = NonNullable<
        DepartmentsSectionDepartmentType['secondaryMembers']
      >[number];

      if (!dep.title) {
        return null;
      }

      const primaryMembers =
        dep.primaryMembers?.map<PrimaryMemberType | null>((member) => {
          const profile = profiles?.find(
            (prof) => prof._id === member.profile?._ref
          );

          if (!profile?.name || !profile.image?.asset._ref) {
            return null;
          }

          const profileSubtitle = profile.subtitle
            ? findInKeyArray(profile.subtitle, [lang, defaultLanguage.id])
            : null;

          return {
            image: sanityImageAsProps(profile.image),
            name: formatProfileName(profile),
            subtitle: member.role
              ? t(`departments:${member.role}`, null, { fallback: member.role })
              : null ?? profileSubtitle?.value,
            action: {
              href: `#${profile._id}`,
              onClick(event) {
                event.preventDefault();
                setProfileOpen(profile);
              }
            }
          };
        }) ?? [];

      const otherMembers =
        dep.otherMembers?.map<OtherMemberType | null>((member) => {
          const profile = profiles?.find(
            (prof) => prof._id === member.profile?._ref
          );

          if (!profile?.name) {
            return null;
          }

          const profileSubtitle = profile.subtitle
            ? findInKeyArray(profile.subtitle, [lang, defaultLanguage.id])
            : null;

          return {
            name: formatProfileName(profile),
            subtitle: member.role ?? member.label ?? profileSubtitle?.value,
            action: {
              href: `#${profile._id}`,
              onClick(event) {
                event.preventDefault();
                setProfileOpen(profile);
              }
            }
          };
        }) ?? [];

      return {
        title: dep.title,
        primaryMembers: flush(primaryMembers),
        secondaryMembers: flush(otherMembers)
      };
    }) ?? []
  );

  return (
    <>
      <ProfileModal
        profile={profileOpen}
        onClose={() => setProfileOpen(null)}
      />
      <UIDepartmentsSection
        title={title}
        subtitle={subtitle}
        departments={entries}
      />
    </>
  );
}
